import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";

const quotaItems = [
  { category: "Electronics", available: 12500, used: 8200, unit: "units", factories: 234 },
  { category: "Fashion & Apparel", available: 50000, used: 31000, unit: "units", factories: 412 },
  { category: "Home & Garden", available: 8000, used: 4100, unit: "units", factories: 178 },
  { category: "Beauty & Skincare", available: 20000, used: 14500, unit: "units", factories: 95 },
  { category: "Sports & Fitness", available: 15000, used: 7800, unit: "units", factories: 143 },
];

const countries = ["Canada", "United Kingdom", "Australia", "Germany", "France", "Japan", "South Korea", "Brazil", "Mexico", "India", "UAE", "Singapore"];

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const overseasSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Overseas Buyer Quota Program — StarBookPrime",
    "description": "International buyers can access exclusive quota allocations from verified US factories. Apply for bulk order quotas from 120+ countries. $500M+ export volume.",
    "url": `${SITE_URL}/overseas`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Overseas Buyer Quota", "item": `${SITE_URL}/overseas` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Overseas Buyer Quota Program",
    "description": "International buyers from 120+ countries can access exclusive quota allocations from verified US factories. Apply for bulk order quotas. $500M+ export volume.",
    "provider": {
      "@type": "Organization",
      "name": "StarBookPrime",
      "url": SITE_URL,
    },
    "serviceType": "International Trade Quota Allocation",
    "areaServed": "Worldwide",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": `${SITE_URL}/overseas`,
      "serviceType": "Online Application",
    },
  },
];

export default function OverseasPage() {
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({ company: "", country: "", category: "", quantity: "", email: "" });

  return (
    <div className="min-h-screen" style={{backgroundColor: "#f5f5f5"}}>
      <SEO
        title="Overseas Buyer Quota Program — International Bulk Orders | StarBookPrime"
        description="International buyers from 120+ countries can access exclusive quota allocations from verified US factories. Apply for bulk order quotas. $500M+ export volume."
        keywords="overseas buyer quota, international bulk order USA, export quota program"
        canonical="/overseas"
        schema={overseasSchema}
      />
      <Navbar />
      <div className="pt-16">
        {/* Hero */}
        <div className="relative h-64 overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/be2fd6e3cc7dfccd4a9f864a089584da.jpg" alt="Overseas" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30 flex items-center px-8 md:px-16">
            <div>
              <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-3">International Program</span>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">Overseas Buyer Quota</h1>
              <p className="text-white/80">Access exclusive bulk allocations from verified US factories</p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[{ value: "120+", label: "Countries Served", icon: "ri-global-line" }, { value: "$500M+", label: "Export Volume", icon: "ri-money-dollar-circle-line" }, { value: "5,000+", label: "Factory Partners", icon: "ri-building-2-line" }, { value: "48hr", label: "Quota Approval", icon: "ri-time-line" }].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-50 text-orange-500 text-lg mx-auto mb-2"><i className={s.icon} /></div>
                <div className="text-2xl font-extrabold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Quota Table */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Available Quotas by Category</h2>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {quotaItems.map((item, i) => {
                  const pct = Math.round((item.used / item.available) * 100);
                  return (
                    <div key={item.category} className={`p-5 ${i < quotaItems.length - 1 ? "border-b border-gray-50" : ""}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900 text-sm">{item.category}</span>
                        <span className="text-xs text-gray-400">{item.factories} factories</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span>{item.used.toLocaleString()} used</span>
                        <span className="font-semibold text-orange-500">{(item.available - item.used).toLocaleString()} available</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct > 80 ? "bg-red-400" : pct > 60 ? "bg-yellow-400" : "bg-green-400"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{pct}% allocated</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Application Form */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Apply for Buyer Quota</h2>
              {!applied ? (
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
                      <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Your company name" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Country *</label>
                      <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 cursor-pointer bg-white">
                        <option value="">Select country</option>
                        {countries.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Category *</label>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 cursor-pointer bg-white">
                        <option value="">Select category</option>
                        {quotaItems.map((q) => <option key={q.category}>{q.category}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Requested Quantity (units) *</label>
                      <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 1000" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Email *</label>
                      <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="business@company.com" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                    </div>
                    <button onClick={() => setApplied(true)} className="w-full py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">Submit Quota Application</button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 text-green-500 text-2xl mx-auto mb-4"><i className="ri-global-line" /></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Application Received!</h3>
                  <p className="text-gray-500 text-sm mb-5">Our international trade team will review your quota request and respond within 48 hours.</p>
                  <button onClick={() => setApplied(false)} className="px-6 py-2.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">Submit Another</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
